import { api, type CreateAircraft, type NewAircraftContext } from '../api';
import { callout, field, hoursVal, input, opt, openDialog, select, todayISO, val, numVal } from '../dialog';
import { hoursInputAttrs } from '../hours';
import { esc, html } from '../ui';

/**
 * Add aircraft.
 *
 * The regime is the first real decision and it drives everything after it —
 * which programme bases are legal, what the contract is signed under, which
 * ARC form comes out, who may release the aircraft. So the form reshapes
 * itself when the regime changes and says, in place, what the consequence is.
 *
 * The dropdowns are built from the rules the server sends rather than a copy
 * kept here. The server enforces them again on submit: a rule that only exists
 * in the browser is not a rule.
 */

/** Sentinel for the "Add a new type…" option the mockup offers. */
const NEW_TYPE = '__new__';

/**
 * The registries, EASA states first.
 *
 * Which regimes are available follows from where the aircraft is registered —
 * Part-ML and Part-M only reach EASA member states — so the two groups are
 * shown apart rather than as one alphabetical list.
 */
function registryOptions(ctx: NewAircraftContext, selected = 'RO'): string {
  const group = (label: string, list: NewAircraftContext['registries']) =>
    list.length
      ? `<optgroup label="${esc(label)}">${list
          .map((r) => opt(r.code, `${r.country} (${r.prefix}${r.separator})`, r.code === selected))
          .join('')}</optgroup>`
      : '';

  return (
    group('EASA member states', ctx.registries.filter((r) => r.easa)) +
    group('Other registries', ctx.registries.filter((r) => !r.easa))
  );
}

/**
 * Puts the separator back when it has been left out — "yrbuc" becomes
 * "YR-BUC". Mirrors the server, which does the same on submit: this one is a
 * convenience so the field reads correctly while it is being filled in.
 */
function registrationHint(ctx: NewAircraftContext, code: string): string {
  const r = ctx.registries.find((x) => x.code === code);
  if (!r) return 'YR-XXX';
  return r.separator === '' ? `${r.prefix}12345` : `${r.prefix}-XXX`;
}

function separatorHint(ctx: NewAircraftContext, code: string): string {
  const r = ctx.registries.find((x) => x.code === code);
  if (!r) return '';
  return r.separator === ''
    ? `${r.country} uses no separator — ${r.prefix}512SR, not ${r.prefix}-512SR`
    : `Leave the hyphen out if you like — it is added for you`;
}

/**
 * Reads the state of registry back out of the registration.
 *
 * The nationality mark is the register, so typing "OK-AAY" says Czechia more
 * reliably than a dropdown left on its default does. Longest prefix wins, which
 * is what tells CS- from C- and HB- from HA-. Mirrors the server.
 */
function inferRegistry(value: string, ctx: NewAircraftContext): NewAircraftContext['registries'][number] | undefined {
  const v = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!v) return undefined;

  let best: NewAircraftContext['registries'][number] | undefined;
  let bestLength = 0;
  for (const r of ctx.registries) {
    const prefix = r.prefix.replace(/-/g, '');
    if (v.length > prefix.length && v.startsWith(prefix) && prefix.length > bestLength) {
      best = r;
      bestLength = prefix.length;
    }
  }
  return best;
}

function applySeparator(value: string, code: string, ctx: NewAircraftContext): string {
  const v = value.trim().toUpperCase().replace(/\s+/g, '');
  const r = ctx.registries.find((x) => x.code === code);
  if (!r || !v) return v;

  const prefix = r.prefix.toUpperCase();
  if (r.separator === '') {
    return v.startsWith(`${prefix}-`) ? prefix + v.slice(prefix.length + 1) : v;
  }
  if (v.startsWith(`${prefix}-`)) return v;
  if (v.startsWith(prefix) && v.length > prefix.length) return `${prefix}-${v.slice(prefix.length)}`;
  return v;
}

/** Fields shown when a type is being added inline. */
function newTypeFields(): string {
  return html`
    <div class="fset">
      <div class="lg">New type</div>
      ${field(
        'Find the type',
        input('typeSearch', '', 'placeholder="Start typing — Cessna 172, DA40, PA-28…" data-type-search autocomplete="off"'),
        'ICAO Doc 8643. Picking one fills in the three fields below',
      )}
      <div class="pick" data-catalog hidden></div>
      <div class="frow3">
        ${field('Manufacturer', input('newManufacturer', '', 'readonly tabindex="-1" placeholder="From the catalogue" maxlength="120"'))}
        ${field('Model', input('newModel', '', 'readonly tabindex="-1" placeholder="From the catalogue" maxlength="120"'))}
        ${field('ICAO designator', input('newDesignator', '', 'readonly tabindex="-1" placeholder="—" style="font-family:var(--mono)" maxlength="8"'))}
      </div>
      ${callout('info', 'i', html`
        The three above come from ICAO Doc 8643 and are not typed by hand — they identify the type, and a
        type identified differently by two people is two types. One designator covers a family: every
        PA-28-181 Archer variant is <b>P28A</b>, which is why the model is chosen from the list rather
        than written.
      `)}
      <div class="frow3">
        ${field('MTOM (kg)', input('newMtom', '', 'inputmode="decimal" style="text-align:right;font-family:var(--mono)"'))}
        ${field('Engines', input('newEngines', '1', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        ${field('Powerplant', select('newPowerplant', [opt('PISTON', 'Piston'), opt('TURBINE', 'Turbine')].join('')))}
      </div>
      <div class="frow">
        ${field('Max occupants', input('newOccupants', '', 'inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
        ${field('TCDS reference', input('newTcds', '', 'maxlength="60"'))}
      </div>
      ${callout('info', 'i', html`
        Whether the type is <b>complex motor-powered</b> is worked out from these figures rather than asked
        for — above 5 700 kg, above 19 passenger seats, or turbojet/multi-turboprop. It decides whether
        Part-ML can reach the aircraft at all, so it is a definition rather than a choice.
      `)}
    </div>
  `;
}

const REGIMES: Array<{ value: string; label: string }> = [
  { value: 'PART_ML', label: 'Part-ML' },
  { value: 'PART_M_G', label: 'Part-M Subpart G' },
  { value: 'FAA_91', label: 'FAA 14 CFR Part 91' },
  { value: 'ANNEX_I', label: 'Annex I (national)' },
];

/**
 * Which regimes the chosen register can carry.
 *
 * Reg. (EU) 1321/2014 Article 1(a) scopes Part-ML and Part-M to aircraft
 * registered in an EASA member state, and 14 CFR Part 91 to the US register.
 * Rather than let the form offer "Part-ML" for a G-registered aeroplane and
 * reject it on submit, the choice that cannot apply is not offered. Part-M
 * stays available on a third-country register because Article 1(b) is a real
 * route — it just has to be justified, which the note below says.
 */
/**
 * Exported for the edit form, which corrects the same two fields.
 *
 * Which regimes a register allows, and which reasons each regime allows, are
 * rules rather than lists — a second copy in the other dialog is a second
 * copy free to fall behind this one.
 */
export function regimesFor(ctx: NewAircraftContext, code: string): typeof REGIMES {
  const r = ctx.registries.find((x) => x.code === code);
  if (!r) return REGIMES;
  return REGIMES.filter((g) => {
    if (g.value === 'PART_ML') return r.easa;
    if (g.value === 'FAA_91') return code === 'US';
    return true;
  });
}

const BASIS_LABEL: Record<string, string> = {
  ML_APPROVED_BY_ORG: 'AMP approved by this organisation — ML.A.302(b)',
  ML_MIP: 'Minimum inspection programme content — ML.A.302(d)',
  M_APPROVED: 'Approved AMP — M.A.302',
  FAA_91_409A: 'Annual + 100-hour — 91.409(a)',
  FAA_PROGRESSIVE: 'Progressive inspection — 91.409(d)',
  ANNEX_I_ACCEPTED: 'Programme accepted by the national authority',
  ANNEX_I_DECLARED: 'Owner-declared programme, where the state allows it',
};

const REGIME_BASIS: Record<string, Array<{ value: string; label: string }>> = {
  PART_ML: [{ value: 'ML_IN_SCOPE', label: 'Within Part-ML scope (ML.1)' }],
  PART_M_G: [
    { value: 'COMPLEX_MOTOR_POWERED', label: 'Complex motor-powered' },
    { value: 'AOC_LISTED', label: 'Listed on a licensed air carrier AOC' },
  ],
  // Only offered on a register outside Reg. (EU) 1321/2014 — see basisFor.
  PART_M_G_THIRD_COUNTRY: [
    { value: 'THIRD_COUNTRY_ARTICLE_1B', label: 'Third-country register, EU-overseen operator — Article 1(b)' },
    { value: 'COMPLEX_MOTOR_POWERED', label: 'Complex motor-powered' },
    { value: 'AOC_LISTED', label: 'Listed on a licensed air carrier AOC' },
  ],
  FAA_91: [{ value: 'THIRD_COUNTRY_REGISTRY', label: 'Third-country registry' }],
  ANNEX_I: [{ value: 'NON_EASA_ANNEX_I', label: 'Annex I — outside the Basic Regulation' }],
};

/** Part-M on a register outside Reg. (EU) 1321/2014 — the Article 1(b) case. */
function needsArticle1b(ctx: NewAircraftContext, regime: string, code: string): boolean {
  const r = ctx.registries.find((x) => x.code === code);
  return regime === 'PART_M_G' && !!r && !r.easa;
}

/** Why this regime — the reasons on offer depend on the register too. */
export function basisFor(ctx: NewAircraftContext, regime: string, code: string) {
  const key = needsArticle1b(ctx, regime, code) ? 'PART_M_G_THIRD_COUNTRY' : regime;
  return REGIME_BASIS[key] ?? [];
}

/** Why the regime list is shorter than it was, said where it happens. */
function registryNote(ctx: NewAircraftContext, code: string): string {
  const r = ctx.registries.find((x) => x.code === code);
  if (!r) return '';
  if (r.easa) return `${r.country} is under Reg. (EU) 1321/2014`;
  if (code === 'US') return 'US register — 14 CFR Part 91';
  return `${r.country} is outside Reg. (EU) 1321/2014, so Part-ML is not available`;
}

/** What choosing this regime actually means, shown where the choice is made. */
function regimeNote(regime: string, ctx: NewAircraftContext): string {
  const rule = ctx.regimeRules[regime];
  switch (regime) {
    case 'PART_ML':
      return callout('ok', '✓', html`
        The airworthiness review certificate will be <b>${esc(rule?.arcForm ?? 'Form 15c')}</b>, issued by
        this organisation under ML.A.901(b)(2) and extendable twice for a year each. Pilot-owner
        maintenance under ML.A.801(b)(3) is available for the Appendix II tasks.
        <br><br>
        An <b>owner-declared</b> AMP is not offered: that route is only open while no CAMO or CAO
        manages the aircraft, and signing the ML.A.201(f) contract closes it.
      `);
    case 'PART_M_G':
      return callout('caution', '!', html`
        A Part-CAO may manage non-complex Part-M aircraft, but <b>not</b> aircraft listed on the AOC of
        an air carrier licensed under Reg. (EC) 1008/2008 — those need a <b>Part-CAMO</b>. With the ARC
        privilege the managing organisation issues <b>Form 15b</b>; without it the outcome is a
        <b>recommendation</b> and the authority issues <b>Form 15a</b>. Pilot-owner maintenance is not
        available.
      `);
    case 'FAA_91':
      return callout('info', 'i', html`
        No ARC. Continuing airworthiness runs on the inspection programme, AD compliance under
        91.403(a), and release by an A&amp;P with Inspection Authorisation. Release documents will be
        <b>FAA 8130-3</b>.
      `);
    default:
      return callout('caution', '!', html`
        Outside the Basic Regulation — national rules apply. Check what the competent authority
        accepts before issuing anything.
      `);
  }
}

export function openAddAircraft(ctx: NewAircraftContext, onDone?: () => void): void {
  // Held outside `body()` so a re-render keeps what the user already chose.
  let regime = 'PART_ML';
  // A brand new organisation has no types at all, so the form opens ready to
  // add one rather than presenting an empty dropdown and no way forward.
  let types = [...ctx.types];
  let addingType = types.length === 0;
  let registry = 'RO';
  // Held here for the same reason as the regime: the body is rebuilt on every
  // refresh, so a chosen type has to survive changing the register.
  let selectedType = '';

  const typeOptions = (selectedId?: string) =>
    [
      ...types.map((t) =>
        opt(
          t.id,
          `${t.manufacturer} ${t.model}${t.designator ? ` (${t.designator})` : ''}${t.isComplex ? ' · complex' : ''}`,
          t.id === selectedId,
        ),
      ),
      opt(NEW_TYPE, 'Add a new type…', addingType),
    ].join('');

  openDialog<CreateAircraft & { newType?: Record<string, unknown> }>({
    wide: true,
    title: 'Add aircraft',
    sub: 'The regulatory regime drives everything downstream — programme basis, ARC form, who may release it',
    primary: 'Add aircraft and continue',

    body: () => {
      const rule = ctx.regimeRules[regime];
      return html`
        <div class="fset">
          <div class="lg">Identity</div>
          <div class="frow3">
            ${field(
              'Registration',
              input('registration', '', `placeholder="${esc(registrationHint(ctx, registry))}" style="font-family:var(--mono);text-transform:uppercase" maxlength="12" data-registration`),
              separatorHint(ctx, registry),
            )}
            ${field('Serial number', input('serialNumber', '', 'placeholder="172S11284" style="font-family:var(--mono)" maxlength="40"'))}
            ${field('Year of manufacture', input('yearOfManufacture', '', 'placeholder="2006" inputmode="numeric"'))}
          </div>
          <div class="frow">
            ${field('Type', select('aircraftTypeId', typeOptions(selectedType), 'data-type'))}
            ${field('Registry', select('registryCountry', registryOptions(ctx, registry), 'data-registry'))}
          </div>
          ${addingType ? newTypeFields() : ''}
        </div>

        <div class="fset">
          <div class="lg">Regulatory</div>
          <div class="frow">
            ${field(
              'Regime',
              select('regime', regimesFor(ctx, registry).map((r) => opt(r.value, r.label, r.value === regime)).join(''), 'data-regime'),
              registryNote(ctx, registry),
            )}
            ${field(
              'Programme basis',
              select('programmeBasis', (rule?.programmeBases ?? []).map((b) => opt(b, BASIS_LABEL[b] ?? b)).join('')),
              'Options follow the regime',
            )}
          </div>
          ${regimeNote(regime, ctx)}
          <div class="frow">
            ${field('Why this regime', select('regimeBasis', basisFor(ctx, regime, registry).map((b) => opt(b.value, b.label)).join('')))}
            ${
              ctx.approvals.length
                ? field(
                    'Managed under',
                    select('managedUnderApprovalId', ctx.approvals.map((a) => opt(a.id, `${a.reference} — ${a.kind}`)).join('')),
                  )
                : ''
            }
          </div>
          ${
            // A third-country aircraft reaches Part-M only through Article 1(b),
            // as one used by an operator a Member State oversees. The server
            // will not take it on trust, so the field appears exactly when it
            // is the thing that makes the combination legal.
            needsArticle1b(ctx, regime, registry)
              ? field(
                  'Article 1(b) justification',
                  input('regimeJustification', '', 'placeholder="Operated by … under the oversight of …" maxlength="300"'),
                  `${ctx.registries.find((r) => r.code === registry)?.country ?? 'This register'} is outside Reg. (EU) 1321/2014 — record which Member State oversees the operator`,
                )
              : ''
          }
          <div class="frow">
            ${field('Owner / customer', select('customerId', ctx.customers.map((c) => opt(c.id, c.name)).join('')))}
            ${field('Base aerodrome', input('baseAerodrome', 'LRBS', 'style="font-family:var(--mono);text-transform:uppercase" maxlength="8"'))}
          </div>
          ${field(
            'Written contract reference',
            input('contractReference', '', 'placeholder="CT-YRXXX-2026" maxlength="60"'),
            'ML.A.201(f) requires a written contract in accordance with Appendix I before management starts',
          )}
        </div>

        <div class="fset">
          <div class="lg">Opening counters</div>
          <div class="frow3">
            ${field('Airframe TSN (h)', input('tsnHours', '', hoursInputAttrs()))}
            ${field('Landings', input('landingsTotal', '', 'placeholder="0" inputmode="numeric" style="text-align:right;font-family:var(--mono)"'))}
            ${field('Counters as of', input('countersAsOf', todayISO(), 'type="date"'))}
          </div>
          ${callout('info', 'i', html`
            Opening counters are recorded once and never edited again — from here they only advance
            when a flight is signed. If they are wrong, correct them with a counter adjustment, which
            is logged.
          `)}
        </div>
      `;
    },

    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-regime]')?.addEventListener('change', (e) => {
        regime = (e.target as HTMLSelectElement).value;
        refresh();
      });
      root.querySelector<HTMLSelectElement>('[data-type]')?.addEventListener('change', (e) => {
        selectedType = (e.target as HTMLSelectElement).value;
        addingType = selectedType === NEW_TYPE;
        refresh();
      });

      // Changing the registry re-renders so the hint and placeholder follow it,
      // and re-applies the separator to whatever has already been typed.
      const regField = root.querySelector<HTMLInputElement>('[data-registration]');
      root.querySelector<HTMLSelectElement>('[data-registry]')?.addEventListener('change', (e) => {
        registry = (e.target as HTMLSelectElement).value;
        // Switching to a non-EASA register while Part-ML is selected would
        // leave an illegal pairing chosen but no longer visible in the list.
        const allowed = regimesFor(ctx, registry);
        if (!allowed.some((r) => r.value === regime)) {
          regime = (allowed[0] ?? REGIMES[0]).value;
        }
        const typed = regField?.value ?? '';
        refresh();
        const after = root.querySelector<HTMLInputElement>('[data-registration]');
        if (after && typed) after.value = applySeparator(typed, registry, ctx);
      });

      // Leaving the registration settles both the register and the hyphen. The
      // mark is the better evidence of the two — someone typing OK-AAY has told
      // us it is Czech, and hyphenating it against a dropdown still sitting on
      // its default is how "OKAAY" got stored unhyphenated and slipped past the
      // duplicate check. So the register follows the registration, not the
      // other way round.
      regField?.addEventListener('blur', (e) => {
        const guess = inferRegistry(regField.value, ctx);
        if (!guess || guess.code === registry) {
          regField.value = applySeparator(regField.value, registry, ctx);
          return;
        }

        registry = guess.code;
        // Part-ML may have just stopped being available — see regimesFor.
        const allowed = regimesFor(ctx, registry);
        if (!allowed.some((r) => r.value === regime)) {
          regime = (allowed[0] ?? REGIMES[0]).value;
        }

        // Re-rendering on blur destroys the field the user was tabbing into, so
        // it is handed the focus again afterwards.
        const next = e.relatedTarget instanceof HTMLElement ? e.relatedTarget.getAttribute('name') : null;
        const typed = regField.value;
        refresh();

        const after = root.querySelector<HTMLInputElement>('[data-registration]');
        if (after) after.value = applySeparator(typed, registry, ctx);
        if (next) root.querySelector<HTMLElement>(`[name="${next}"]`)?.focus();
      });

      bindTypeCatalog(root);
    },

    collect: (root) => {
      const registration = val(root, 'registration').toUpperCase();
      if (!registration) return { error: 'A registration is required.' };
      if (!val(root, 'serialNumber')) return { error: 'A serial number is required.' };
      if (!val(root, 'contractReference')) {
        return {
          error: 'The written contract reference is required — ML.A.201(f) needs it in place before management starts.',
        };
      }

      const justification = val(root, 'regimeJustification');
      if (needsArticle1b(ctx, val(root, 'regime'), val(root, 'registryCountry')) && !justification) {
        return {
          error: 'An aircraft on a third-country register only falls under Part-M through Article 1(b). Say which Member State oversees the operator.',
        };
      }

      const approval = val(root, 'managedUnderApprovalId');
      const typeId = val(root, 'aircraftTypeId');

      let newType: Record<string, unknown> | undefined;
      if (typeId === NEW_TYPE) {
        const manufacturer = val(root, 'newManufacturer');
        const model = val(root, 'newModel');
        const designator = val(root, 'newDesignator');
        if (!manufacturer || !model || !designator) {
          return {
            error: 'Search for the type and pick it from the list. The manufacturer, model and ICAO designator come from ICAO Doc 8643 — an aircraft cannot be added without them.',
          };
        }
        newType = {
          manufacturer,
          model,
          designator,
          tcdsReference: val(root, 'newTcds') || undefined,
          mtomKg: numVal(root, 'newMtom'),
          maxOccupants: numVal(root, 'newOccupants'),
          engineCount: numVal(root, 'newEngines') ?? 1,
          isTurbine: val(root, 'newPowerplant') === 'TURBINE',
        };
      }

      return {
        registration,
        serialNumber: val(root, 'serialNumber'),
        yearOfManufacture: numVal(root, 'yearOfManufacture'),
        aircraftTypeId: typeId,
        newType,
        registryCountry: val(root, 'registryCountry'),
        regime: val(root, 'regime'),
        regimeBasis: val(root, 'regimeBasis') || undefined,
        regimeJustification: justification || undefined,
        managedUnderApprovalId: approval || undefined,
        programmeBasis: val(root, 'programmeBasis'),
        customerId: val(root, 'customerId'),
        baseAerodrome: val(root, 'baseAerodrome') || undefined,
        contractReference: val(root, 'contractReference'),
        tsnHours: hoursVal(root, 'tsnHours'),
        landingsTotal: numVal(root, 'landingsTotal'),
        countersAsOf: val(root, 'countersAsOf') || undefined,
      };
    },

    submit: async (payload) => {
      if (payload.aircraftTypeId === NEW_TYPE) {
        // Types are global reference data, so this either creates one or finds
        // the one already on file — either way the aircraft lands on a real
        // type rather than a duplicate of an existing type certificate.
        const type = await api.aircraft.createType(payload.newType!);
        types = [...types.filter((t) => t.id !== type.id), type];
        payload.aircraftTypeId = type.id;
      }
      delete payload.newType;
      return api.aircraft.create(payload);
    },
    done: (r) => `${r.registration} added — set up the maintenance programme next`,
    onDone,
  });
}

/** Fetches the context, then opens. Used by every "Add aircraft" button. */
export async function addAircraft(onDone?: () => void): Promise<void> {
  const ctx = await api.aircraft.context();
  openAddAircraft(ctx, onDone);
}


/**
 * The ICAO catalogue lookup on the new-type fields.
 *
 * It fills manufacturer, model and designator and stops there: mass, seating
 * and powerplant are not in the catalogue, and they are what decide whether
 * the type is complex motor-powered — a regulatory determination rather than a
 * lookup.
 */
function bindTypeCatalog(root: HTMLElement): void {
  const search = root.querySelector<HTMLInputElement>('[data-type-search]');
  const panel = root.querySelector<HTMLElement>('[data-catalog]');
  if (!search || !panel) return;

  let timer: number | undefined;
  let seq = 0;

  const fill = (manufacturer: string, model: string, designator: string) => {
    const set = (name: string, value: string) => {
      const el = root.querySelector<HTMLInputElement>(`[name="${name}"]`);
      if (el) el.value = value;
    };
    set('newManufacturer', manufacturer);
    set('newModel', model);
    set('newDesignator', designator);
    panel.hidden = true;
    search.value = `${manufacturer} ${model}`.trim();
  };

  search.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const q = search.value.trim();
      if (q.length < 2) {
        panel.hidden = true;
        return;
      }
      const mine = ++seq;
      try {
        const r = await api.aircraft.typeCatalog(q);
        if (mine !== seq) return;
        panel.innerHTML = r.results.length
          ? r.results
              .map(
                (t) => html`<label data-pick="${esc(t.designator)}|${esc(t.manufacturer)}|${esc(t.model)}">
                  <span class="pb">
                    <span class="pt">${esc(t.manufacturer)} ${esc(t.model)}</span>
                    <span class="pm">ICAO ${esc(t.designator)}</span>
                  </span>
                </label>`,
              )
              .join('')
          : '<label><span class="pb"><span class="pt">Nothing in the catalogue matches — type it in below.</span></span></label>';
        panel.hidden = false;

        panel.querySelectorAll<HTMLElement>('[data-pick]').forEach((el) =>
          el.addEventListener('click', () => {
            const [designator, manufacturer, model] = el.dataset.pick!.split('|');
            fill(manufacturer, model, designator);
          }),
        );
      } catch {
        if (mine === seq) panel.hidden = true;
      }
    }, 200);
  });
}
